import './CompressionHistories.scss';
import { useState } from 'react';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { HistoryRawMaterials } from 'components/organisms/HistoryRawMaterials';
import { HistoryProcessedProducts } from 'components/organisms/HistoryProcessedProducts';
import { HistoryStorageSite } from 'components/organisms/HistoryStorageSite';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getFormatDateWithDeviceTimezone } from 'ts/formatDate';
import { TabletTab } from 'components/molecules/TabletTab';
import { useTranslation } from 'react-i18next';
import { createAxios } from 'ts/createAxios';
import Csv from 'util/csv';
import { handleError } from 'util/errorHandler';
import {
  RawMaterialHistoryItem,
  ProcessedProductHistoryItem,
  StorageSiteHistoryItem,
} from 'util/Types';
import { API_ENDPOINTS } from 'util/endPoints';
import { setIsExporting } from 'redux/slices/historiesCsvExportSlice';
import { CompressionSiteType } from 'util/Enums';
import { normalizeNumber } from 'util/commons';
import { DATE_FORMAT } from 'util/ConstantValues';

const CompressionHistories = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [rawMaterialsDateRange, setRawMaterialsDateRange] =
    useState<[string, string]>();
  const [processedProductsDateRange, setProcessedProductsDateRange] =
    useState<[string, string]>();
  const [storageDateRange, setStorageDateRange] = useState<[string, string]>();

  const isStorage = user?.siteType === CompressionSiteType.Storage;

  const tabNames = [
    t('histories.tabs.raw_materials_list'),
    t('histories.tabs.processed_products_list'),
  ];

  const handleDirectDownload = async () => {
    dispatch(setIsExporting(true));
    try {
      if (isStorage) {
        const res = await createAxios({ notShowLoading: true }).get<
          (Omit<StorageSiteHistoryItem, 'storageLocation'> & {
            storageName?: string;
          })[]
        >(API_ENDPOINTS.WORK_HISTORIES_STORAGE_SITE_HISTORY_EXPORT, {
          params: {
            dateFrom: storageDateRange?.[0],
            dateTo: storageDateRange?.[1],
          },
        });
        const data = res.data;
        const headers = [
          t('histories.label_csv_received_date_and_time'),
          t('common_history_field.place'),
          t('histories.label_item_name'),
          t('common_history_field.material_name'),
          t('common_history_field.weight'),
          t('storage_location_registration.receiving_method'),
          t('histories.label_resource_id'),
          t('storage_location_registration.local_storage'),
          t('storage_location_registration.storage_update_datetime'),
          t('histories.label_status'),
          t('storage_location_registration.shipment_date'),
        ];
        const csvData = data.map((item) => [
          item.receivedDatetime
            ? getFormatDateWithDeviceTimezone(item.receivedDatetime)
            : '-',
          item.fromPlace || '-',
          item.itemName || '-',
          item.materialName || '-',
          item.weight
            ? normalizeNumber({
                value: item.weight,
              }) + 'kg'
            : '-',
          item.receivingMethod
            ? t(
                `histories.storage_receiving_method.${item.receivingMethod}`,
                item.receivingMethod as string
              )
            : '-',
          item.resourceId || '-',
          item.storageName || t('storage_location_registration.not_set'),
          item.storageUpdateDatetime
            ? getFormatDateWithDeviceTimezone(item.storageUpdateDatetime)
            : '-',
          item.status
            ? t(
                `histories.storage_site_status.${item.status}`,
                item.status as string
              )
            : '-',
          item.shipmentDate
            ? getFormatDateWithDeviceTimezone(
                item.shipmentDate,
                DATE_FORMAT.full_date
              )
            : '-',
        ]);
        new Csv({ data: csvData, headers }).downLoadCsv({
          filename:
            user.siteName +
            '_' +
            t('compressions.storage_site_histories.title'),
        });
      } else {
        const isTab0 = tabIndex === 0;
        if (isTab0) {
          const res = await createAxios({ notShowLoading: true }).get<
            RawMaterialHistoryItem[]
          >(API_ENDPOINTS.WORK_HISTORIES_COMPRESSION_RESOURCE_RECEIVED_EXPORT, {
            params: {
              dateFrom: rawMaterialsDateRange?.[0],
              dateTo: rawMaterialsDateRange?.[1],
            },
          });
          const data = res.data;
          const headers = [
            t('histories.label_received_registered_date_time'),
            t('common_history_field.place'),
            t('common_history_field.material_name'),
            t('histories.label_item_name'),
            t('common_history_field.weight'),
            t('histories.label_resource_id'),
            t('histories.label_status'),
            t('histories.label_inventory'),
          ];
          const csvData = data.map((item) => [
            getFormatDateWithDeviceTimezone(item.receivedDatetime),
            item.place,
            item.materialName,
            item.itemName || '-',
            item.weight
              ? normalizeNumber({
                  value: item.weight,
                }) + 'kg'
              : '-',
            item.id,
            t(`histories.raw_material_status.${item.status}`, item.status),
            item.inventoryWeight ? item.inventoryWeight : '-',
          ]);
          new Csv({ data: csvData, headers }).downLoadCsv({
            filename:
              user.siteName + '_' + t('histories.tabs.raw_materials_list'),
          });
        } else {
          const res = await createAxios({ notShowLoading: true }).get<
            ProcessedProductHistoryItem[]
          >(
            API_ENDPOINTS.WORK_HISTORIES_COMPRESSION_PROCESSED_PRODUCTS_EXPORT,
            {
              params: {
                dateFrom: processedProductsDateRange?.[0],
                dateTo: processedProductsDateRange?.[1],
              },
            }
          );
          const data = res.data;
          const headers = [
            t('histories.label_processing_registered_date_and_time'),
            t('common_history_field.material_name'),
            t('common_history_field.type'),
            t('common_history_field.label_quality_grade'),
            t('pre_regist_resource_select.input_weight'),
            t('common_history_field.weight'),
            t('common_history_field.registration_id'),
            t('histories.label_status'),
          ];
          const csvData = data.map((item) => [
            getFormatDateWithDeviceTimezone(item.registDatetime),
            item.materialName,
            item.kind,
            item.grade,
            item.inputResourceWeight
              ? normalizeNumber({
                  value: item.inputResourceWeight,
                }) + 'kg'
              : '-',
            item.weight
              ? normalizeNumber({
                  value: item.weight,
                }) + 'kg'
              : '-',
            item.id,
            t(`histories.processed_product_status.${item.status}`, item.status),
          ]);
          new Csv({ data: csvData, headers }).downLoadCsv({
            filename:
              user.siteName + '_' + t('histories.tabs.processed_products_list'),
          });
        }
      }
    } catch (e) {
      handleError(e);
    } finally {
      dispatch(setIsExporting(false));
    }
  };

  if (isStorage) {
    return (
      <div>
        <PrimaryTemplate
          h1={t('compressions.storage_site_histories.title')}
          download={handleDirectDownload}
          className="history"
        >
          <div className="storage-site-history">
            <HistoryStorageSite
              onAppliedDateRangeChange={setStorageDateRange}
            />
          </div>
        </PrimaryTemplate>
      </div>
    );
  }

  return (
    <div>
      <PrimaryTemplate
        h1={t('nav.histories')}
        download={handleDirectDownload}
        className="history"
      >
        <TabletTab
          tabItems={[tabNames[0], tabNames[1]]}
          setTabIndex={setTabIndex}
          defaultTabIndex={tabIndex}
          renderContent={({ index }) => {
            return (
              <div className="scroll-contents">
                {index === 0 && (
                  <HistoryRawMaterials
                    onAppliedDateRangeChange={setRawMaterialsDateRange}
                  />
                )}
                {index === 1 && (
                  <HistoryProcessedProducts
                    onAppliedDateRangeChange={setProcessedProductsDateRange}
                  />
                )}
              </div>
            );
          }}
        />
      </PrimaryTemplate>
    </div>
  );
};

export default CompressionHistories;
